/**
 * Rider flags, resolved from the nationality code.
 *
 * **Why this does not use the stored flag URL.** `ResultFS` carries `riderBadge`,
 * which resolves to the rider's `country.flagUrl` — a per-record copy of an image
 * URL that has to agree with a separate copy of the nationality code, and does
 * not. ED-WEB audited this on the MLSJ roster: of 48 entries, 32 had no flag at
 * all, 4 held a team badge instead of a country flag, and one read `ISR` while
 * pointing at the flag of the USA. Deriving the flag from the code removes that
 * class of bug instead of fixing instances of it — a rider whose nationality is
 * right has a right flag, always.
 *
 * `riderBadge` is also overloaded: on team classes it holds the team logo, and on
 * Mexican national classes `riderAcronym` is a *club* acronym rather than a
 * country code. Both simply miss the lookup below and render no flag, which is the
 * intended outcome.
 *
 * Ported from ED-WEB `src/mlsj/app/models/country-flags.ts`; the assets in
 * `public/flags/` are the same 160px PNGs, named by IOC code. Codes and files must
 * stay in step — adding a flag means adding both.
 */

/**
 * Every IOC/FEI code an asset exists for. The FEI uses IOC codes (`GER`, `SUI`,
 * `NED`), not ISO ones, and the two genuinely differ for many of the nations that
 * turn up in results.
 */
const FLAG_CODES: ReadonlySet<string> = new Set([
  'ARG',
  'AUS',
  'AUT',
  'AZE',
  'BEL',
  'BLR',
  'BRA',
  'BUL',
  'CAN',
  'CHI',
  'CHN',
  'COL',
  'CRC',
  'CRO',
  'CYP',
  'CZE',
  'DEN',
  'DOM',
  'ECU',
  'EGY',
  'ESA',
  'ESP',
  'EST',
  'FIN',
  'FRA',
  'GBR',
  'GEO',
  'GER',
  'GRE',
  'GUA',
  'HKG',
  'HUN',
  'INA',
  'IND',
  'IRI',
  'IRL',
  'ISL',
  'ISR',
  'ITA',
  'JOR',
  'JPN',
  'KAZ',
  'KOR',
  'KSA',
  'KUW',
  'LAT',
  'LBN',
  'LTU',
  'LUX',
  'MAR',
  'MAS',
  'MEX',
  'MON',
  'NED',
  'NOR',
  'NZL',
  'PAN',
  'PER',
  'PHI',
  'POL',
  'POR',
  'PUR',
  'QAT',
  'ROU',
  'RSA',
  'RUS',
  'SLO',
  'SRB',
  'SUI',
  'SVK',
  'SWE',
  'THA',
  'TPE',
  'TUR',
  'UAE',
  'UKR',
  'URU',
  'USA',
  'VEN',
  'ZIM',
]);

/**
 * The path to a flag, or `undefined` for a code with no asset.
 *
 * Undefined rather than a placeholder image: a wrong-but-present flag reads as
 * fact, where a missing one reads as missing.
 *
 * The path is relative on purpose — the widget is served from the hosting root,
 * and a leading slash would break a preview channel served under a sub-path.
 */
export function flagAssetFor(nationality: string | null | undefined): string | undefined {
  const code = (nationality ?? '').trim().toUpperCase();
  if (!code || !FLAG_CODES.has(code)) return undefined;
  return `flags/${code.toLowerCase()}.png`;
}
